import { useState } from "react"

import { isAlphaNumericKey, keyCodes } from "@silevis/reactgrid"
import "./customCellStyle.css"
import "./dropdownCellTemplateStyle.css"

import { Autocomplete, ClickAwayListener, Grid, ListItem, TextField } from "@mui/material"
import HighlightCell from "@/sheet/HighlightCell"
import { getLineItemRate } from "@/sheet/bidStore"
import { formattedCurrency } from "@/utils"

export default class DynamicRateTemplate {
    constructor (highlights, options) {
        this.highlights = highlights
        this.options = options
    }

    getClassName(cell) {
        return "dynamic-rate-cell"
    }

    getStyle() {
        return {
            overflow: "visible"
        }
    }

    getCompatibleCell(uncertainCell) {
        return { ...uncertainCell, enableEditMode: true};
    }

    handleKeyDown(
        cell,
        keyCode,
        ctrl,
        shift,
        alt
    ){
        if (!ctrl && !alt && isAlphaNumericKey(keyCode) || keyCode == "onClick"){
            const char = String.fromCharCode(keyCode)
            const updatedCell = this.update(cell, { ...cell, text: char, value: parseFloat(char) })
            return { updatedCell, enableEditMode: true }
        }
        return {
            cell,
            enableEditMode: keyCode == keyCodes.POINTER || keyCode == keyCodes.ENTER
        }
    }

    update(cell, cellToMerge) {
        return this.getCompatibleCell({...cell, text: cellToMerge.text, value: cellToMerge.value});
    }

    render(cell, isInEditMode, onCellChanged = (cell, commit) => {}) {
        const cellHighlights = this.highlights.filter(h=>(h.rowId == cell.rowId) && (h.columnId == cell.columnId))
        const cellOptions = this.options.filter(option => option.currency == cell.value.currency).map(option => ({
            ...option,
            vendor_rate: option.vendor_rate
        }))
        return (
            <CustomDropdownCell 
                cell={cell}
                highlights={cellHighlights}
                options={cellOptions}
                onCommitCellChange={(newCellValue)=>{
                    onCellChanged(
                        this.getCompatibleCell({ ...cell, text: newCellValue, value: {...newCellValue, rate_type: cell.value.rate_type}}),
                        true
                    )
                }}
            />
        )
    }
}

const CustomDropdownCell = ({cell, options, onCommitCellChange, highlights}) => {
    const [ hover, setHover ] = useState() 
    const handleMouseEnter = () => {
        setHover(true)
    }
    const handleMouseLeave = () => {
        setHover(false)
    }
    return (
        <div style={{width: "100%", height: "100%"}} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <CustomAutocomplete
                cell={cell}
                options={options}
                onCommitCellChange={onCommitCellChange}
            />
            {(highlights.length > 0) && <HighlightCell highlights={highlights} hover={hover}/>}
        </div>
    )
}

const CustomAutocomplete = ({cell, options, onCommitCellChange}) => {
    const [ editing, setEditing ] = useState(false)
    const [ selectedValue, setSelectedValue ] = useState(null)

    const handleClickText = () => {
        if (!cell.nonEditable) {
            setEditing(true)
        }
    }

    const handleAutocompleteChange = (event, newValue) => {
        setEditing(false)
        setSelectedValue(newValue)
        onCommitCellChange(newValue)
    }

    const handleAutocompleteClear = (event, newValue, reason) => {
        if (reason == "clear"){
            onCommitCellChange(null)
            setSelectedValue(null)
        }
    } 

    const handleInputKeyDown = (e) => {
        e.stopPropagation()
    }

    return (
        <Grid container alignItems="center" width="100%" height="100%">
            <Grid item width={"100%"}>
                <ClickAwayListener onClickAway={()=>{setEditing(false)}}>
                    {editing ?
                    <Autocomplete
                        freeSolo
                        disableClearable
                        open
                        style={{padding: 0}}
                        value={selectedValue || cell.value}
                        sx={{width: "100%"}}
                        disablePortal
                        options={options}
                        getOptionLabel={(option)=>option.name}
                        renderOption={(props, option, { selected }) => {
                            return getOptionComponent(props, option)
                        }}
                        renderInput={(params) =>
                            <TextField
                                sx={{border: "none", outline: "none"}}
                                autoFocus
                                {...params}
                                onPointerDown={(e)=>{e.stopPropagation()}}
                                inputProps={{...params.inputProps, style: {fontSize: 12, border: "none", padding: 2, paddingLeft: 6}}}
                                InputProps={{...params.InputProps, style: {padding: 0, fontSize: 12}}}
                            />
                        }
                        onKeyDown={handleInputKeyDown}
                        onChange={handleAutocompleteChange}
                        onInputChange={handleAutocompleteClear}
                        onPointerDown={(e) => {e.stopPropagation()}}
                        isOptionEqualToValue={(option, value) => option.id == value.id}
                    /> :
                    <div style={{textAlign: "right", cursor: cell.nonEditable ? "default" : "pointer"}} onClick={handleClickText}>
                        {formattedCurrency(getLineItemRate(
                            0,
                            cell.value,
                            cell.value.rate_type,
                        ))}
                    </div>
                    }
                </ClickAwayListener>
            </Grid>
        </Grid>
    )
}

const getOptionComponent = (props, option) => {
    if (!option) {
        return <div style={{minHeight: "20px", width: "100%"}}/>
    }

    return (
        <ListItem {...props} key={option.id} style={{padding: 0, margin: 4, fontSize: 12}}>
            <div style={{minHeight: "20px", width: "100%"}}>
                {option.name}
            </div>
        </ListItem>
    )
}
